import { useState } from 'react'
import {
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Select,
  Card,
  Space,
  Divider,
  message,
  Tooltip,
  Badge,
  Typography,
} from 'antd'
import {
  MailOutlined,
  TwitterOutlined,
  SendOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { accountApi } from '../api/client'
import type { AccountState } from '../types'

const { Text } = Typography

// 账户类型
type AccountType = 'email' | 'twitter' | 'telegram'

interface AccountConfigFormProps {
  type: AccountType
  account?: AccountState
  onSuccess?: () => void
  onCancel?: () => void
}

// 邮件账户配置表单
export function EmailAccountForm({ account, onSuccess, onCancel }: Omit<AccountConfigFormProps, 'type'>) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true)
    try {
      const accountData = {
        account_name: values.account_name as string,
        account_type: 'email',
        config: {
          smtp_host: values.smtp_host,
          smtp_port: values.smtp_port,
          smtp_tls: values.smtp_tls,
          username: values.username,
          password: values.password,
          display_name: values.display_name,
          imap_host: values.imap_host,
          imap_port: values.imap_port,
          imap_username: values.imap_username || values.username,
          imap_password: values.imap_password || values.password,
        },
        daily_limit: values.daily_limit,
        hourly_limit: values.hourly_limit,
      }

      if (account) {
        await accountApi.update(account.id, accountData)
        message.success('账户更新成功')
      } else {
        await accountApi.create(accountData)
        message.success('账户创建成功')
      }
      onSuccess?.()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || '保存失败'
      message.error(`保存失败: ${errorMsg}`)
      console.error('Account save error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async () => {
    try {
      await form.validateFields(['smtp_host', 'smtp_port', 'username', 'password'])
    } catch {
      message.warning('请先填写必填字段')
      return
    }

    setTesting(true)
    try {
      const values = form.getFieldsValue()
      const result = await accountApi.testConnection({
        type: 'email',
        config: {
          smtp_host: values.smtp_host,
          smtp_port: values.smtp_port,
          smtp_tls: values.smtp_tls,
          username: values.username,
          password: values.password,
        },
      })
      if (result.success) {
        message.success('连接测试成功')
      } else {
        message.error(result.message || '连接测试失败')
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || '连接测试失败'
      message.error(`连接测试失败: ${errorMsg}`)
      console.error('Connection test error:', error)
    } finally {
      setTesting(false)
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        smtp_host: 'smtp.gmail.com',
        smtp_port: 587,
        smtp_tls: true,
        imap_host: 'imap.gmail.com',
        imap_port: 993,
        daily_limit: 50,
        hourly_limit: 10,
        ...account,
      }}
      onFinish={handleSubmit}
    >
      <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
        <Form.Item
          name="account_name"
          label="账户名称"
          rules={[{ required: true, message: '请输入账户名称' }]}
        >
          <Input placeholder="例如: primary_email" />
        </Form.Item>

        <Form.Item
          name="display_name"
          label="发件人显示名称"
          rules={[{ required: true, message: '请输入显示名称' }]}
        >
          <Input placeholder="例如: Donald | TaskOn" />
        </Form.Item>
      </Card>

      <Card title="SMTP配置 (发送邮件)" size="small" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Form.Item
            name="smtp_host"
            label="SMTP服务器"
            style={{ width: '60%', marginBottom: 0 }}
            rules={[{ required: true, message: '请输入SMTP服务器' }]}
          >
            <Input placeholder="smtp.gmail.com" />
          </Form.Item>
          <Form.Item
            name="smtp_port"
            label="端口"
            style={{ width: '25%', marginBottom: 0 }}
            rules={[{ required: true, message: '请输入端口' }]}
          >
            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="smtp_tls"
            label="TLS"
            style={{ width: '15%', marginBottom: 0 }}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          name="username"
          label="邮箱地址"
          rules={[{ required: true, type: 'email', message: '请输入有效的邮箱地址' }]}
        >
          <Input prefix={<MailOutlined />} placeholder="your@email.com" />
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <Space>
              密码
              <Tooltip title="Gmail需要使用应用专用密码">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: '请输入密码' }]}
        >
          <Input.Password
            prefix={<SafetyOutlined />}
            placeholder="应用专用密码"
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
      </Card>

      <Card title="IMAP配置 (接收回复)" size="small" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Form.Item
            name="imap_host"
            label="IMAP服务器"
            style={{ width: '70%', marginBottom: 0 }}
          >
            <Input placeholder="imap.gmail.com" />
          </Form.Item>
          <Form.Item
            name="imap_port"
            label="端口"
            style={{ width: '30%', marginBottom: 0 }}
          >
            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          name="imap_username"
          label="IMAP用户名 (留空使用SMTP用户名)"
        >
          <Input placeholder="默认使用SMTP用户名" />
        </Form.Item>

        <Form.Item
          name="imap_password"
          label="IMAP密码 (留空使用SMTP密码)"
        >
          <Input.Password placeholder="默认使用SMTP密码" />
        </Form.Item>
      </Card>

      <Card title="发送限制" size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Form.Item name="daily_limit" label="每日限额">
            <InputNumber min={1} max={500} />
          </Form.Item>
          <Form.Item name="hourly_limit" label="每小时限额">
            <InputNumber min={1} max={100} />
          </Form.Item>
        </Space>
      </Card>

      <Divider />

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {account ? '更新账户' : '添加账户'}
          </Button>
          <Button onClick={handleTest} loading={testing}>
            测试连接
          </Button>
          {onCancel && <Button onClick={onCancel}>取消</Button>}
        </Space>
      </Form.Item>
    </Form>
  )
}

// Twitter账户配置表单
export function TwitterAccountForm({ account, onSuccess, onCancel }: Omit<AccountConfigFormProps, 'type'>) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true)
    try {
      const accountData = {
        account_name: values.account_name as string,
        account_type: 'twitter',
        config: {
          api_key: values.api_key,
          api_secret: values.api_secret,
          access_token: values.access_token,
          access_token_secret: values.access_token_secret,
          bearer_token: values.bearer_token,
        },
        daily_limit: values.daily_limit,
      }

      if (account) {
        await accountApi.update(account.id, accountData)
        message.success('账户更新成功')
      } else {
        await accountApi.create(accountData)
        message.success('账户创建成功')
      }
      onSuccess?.()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || '保存失败'
      message.error(`保存失败: ${errorMsg}`)
      console.error('Account save error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async () => {
    try {
      await form.validateFields(['api_key', 'api_secret', 'access_token', 'access_token_secret'])
    } catch {
      message.warning('请先填写必填字段')
      return
    }

    setTesting(true)
    try {
      const values = form.getFieldsValue()
      const result = await accountApi.testConnection({
        type: 'twitter',
        config: {
          api_key: values.api_key,
          api_secret: values.api_secret,
          access_token: values.access_token,
          access_token_secret: values.access_token_secret,
          bearer_token: values.bearer_token,
        },
      })
      if (result.success) {
        message.success('连接测试成功')
      } else {
        message.error(result.message || '连接测试失败')
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || '连接测试失败'
      message.error(`连接测试失败: ${errorMsg}`)
      console.error('Connection test error:', error)
    } finally {
      setTesting(false)
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        daily_limit: 50,
        ...account,
      }}
      onFinish={handleSubmit}
    >
      <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
        <Form.Item
          name="account_name"
          label="账户名称"
          rules={[{ required: true, message: '请输入账户名称' }]}
        >
          <Input placeholder="例如: twitter_main" />
        </Form.Item>
      </Card>

      <Card title="API凭证" size="small" style={{ marginBottom: 16 }}>
        <Form.Item
          name="api_key"
          label="API Key"
          rules={[{ required: true, message: '请输入API Key' }]}
        >
          <Input.Password prefix={<TwitterOutlined />} placeholder="API Key" />
        </Form.Item>

        <Form.Item
          name="api_secret"
          label="API Secret"
          rules={[{ required: true, message: '请输入API Secret' }]}
        >
          <Input.Password placeholder="API Secret" />
        </Form.Item>

        <Form.Item
          name="access_token"
          label="Access Token"
          rules={[{ required: true, message: '请输入Access Token' }]}
        >
          <Input.Password placeholder="Access Token" />
        </Form.Item>

        <Form.Item
          name="access_token_secret"
          label="Access Token Secret"
          rules={[{ required: true, message: '请输入Access Token Secret' }]}
        >
          <Input.Password placeholder="Access Token Secret" />
        </Form.Item>

        <Form.Item
          name="bearer_token"
          label={
            <Space>
              Bearer Token
              <Tooltip title="用于只读操作，可选">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Input.Password placeholder="Bearer Token (可选)" />
        </Form.Item>
      </Card>

      <Card title="发送限制" size="small" style={{ marginBottom: 16 }}>
        <Form.Item name="daily_limit" label="每日DM限额">
          <InputNumber min={1} max={200} />
        </Form.Item>
      </Card>

      <Divider />

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {account ? '更新账户' : '添加账户'}
          </Button>
          <Button onClick={handleTest} loading={testing}>
            验证凭证
          </Button>
          {onCancel && <Button onClick={onCancel}>取消</Button>}
        </Space>
      </Form.Item>
    </Form>
  )
}

// Telegram账户配置表单
export function TelegramAccountForm({ account, onSuccess, onCancel }: Omit<AccountConfigFormProps, 'type'>) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true)
    try {
      const accountData = {
        account_name: values.account_name as string,
        account_type: 'telegram',
        config: {
          api_id: values.api_id,
          api_hash: values.api_hash,
          phone: values.phone,
        },
        daily_limit: values.daily_limit,
        hourly_limit: values.hourly_limit,
      }

      if (account) {
        await accountApi.update(account.id, accountData)
        message.success('账户更新成功')
      } else {
        await accountApi.create(accountData)
        message.success('账户创建成功')
      }
      onSuccess?.()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || '保存失败'
      message.error(`保存失败: ${errorMsg}`)
      console.error('Account save error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        daily_limit: 30,
        hourly_limit: 5,
        ...account,
      }}
      onFinish={handleSubmit}
    >
      <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
        <Form.Item
          name="account_name"
          label="账户名称"
          rules={[{ required: true, message: '请输入账户名称' }]}
        >
          <Input placeholder="例如: tg_main" />
        </Form.Item>

        <Form.Item
          name="phone"
          label="手机号"
          rules={[{ required: true, message: '请输入手机号' }]}
        >
          <Input prefix={<SendOutlined />} placeholder="+86 138xxxx" />
        </Form.Item>
      </Card>

      <Card title="API凭证" size="small" style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          从 <a href="https://my.telegram.org" target="_blank" rel="noopener noreferrer">my.telegram.org</a> 获取API凭证
        </Text>

        <Form.Item
          name="api_id"
          label="API ID"
          rules={[{ required: true, message: '请输入API ID' }]}
        >
          <Input placeholder="API ID (数字)" />
        </Form.Item>

        <Form.Item
          name="api_hash"
          label="API Hash"
          rules={[{ required: true, message: '请输入API Hash' }]}
        >
          <Input.Password placeholder="API Hash" />
        </Form.Item>
      </Card>

      <Card title="发送限制" size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Form.Item name="daily_limit" label="每日DM限额">
            <InputNumber min={1} max={100} />
          </Form.Item>
          <Form.Item name="hourly_limit" label="每小时限额">
            <InputNumber min={1} max={20} />
          </Form.Item>
        </Space>
      </Card>

      <Divider />

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {account ? '更新账户' : '添加账户'}
          </Button>
          {onCancel && <Button onClick={onCancel}>取消</Button>}
        </Space>
      </Form.Item>

      <Text type="secondary">
        注意: 保存后需要使用QR码扫描登录Telegram账户
      </Text>
    </Form>
  )
}

// 账户健康状态显示组件
interface AccountHealthBadgeProps {
  health: string
  dailyUsage?: number
  dailyLimit?: number
}

export function AccountHealthBadge({ health, dailyUsage, dailyLimit }: AccountHealthBadgeProps) {
  const getHealthStatus = () => {
    switch (health) {
      case 'healthy':
        return { status: 'success' as const, text: '健康', icon: <CheckCircleOutlined /> }
      case 'warning':
        return { status: 'warning' as const, text: '警告', icon: <ExclamationCircleOutlined /> }
      case 'restricted':
        return { status: 'error' as const, text: '受限', icon: <ExclamationCircleOutlined /> }
      case 'suspended':
        return { status: 'error' as const, text: '暂停', icon: <ExclamationCircleOutlined /> }
      case 'banned':
        return { status: 'error' as const, text: '封禁', icon: <ExclamationCircleOutlined /> }
      case 'warming':
        return { status: 'processing' as const, text: '养号中', icon: <QuestionCircleOutlined /> }
      default:
        return { status: 'default' as const, text: '未知', icon: <QuestionCircleOutlined /> }
    }
  }

  const { status, text, icon } = getHealthStatus()

  return (
    <Space>
      <Badge status={status} text={text} />
      {dailyUsage !== undefined && dailyLimit !== undefined && (
        <Text type="secondary">
          今日: {dailyUsage}/{dailyLimit}
        </Text>
      )}
    </Space>
  )
}

// 导出所有表单组件
export const AccountConfigForms = {
  email: EmailAccountForm,
  twitter: TwitterAccountForm,
  telegram: TelegramAccountForm,
}
